import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader } from 'lucide-react';
import { gymDarkHeaderBar } from '../gymPageTheme';
import MemberCheckboxList from './MemberCheckboxList';
import CourseSessionSelector from './CourseSessionSelector';
import EnrollmentPreview from './EnrollmentPreview';

export default function BulkEnrollmentPage() {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const { isDark } = useTheme();
    const navigate = useNavigate();
    
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [selectedSessions, setSelectedSessions] = useState([]);
    const [sendNotification, setSendNotification] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const handleConfirm = async () => {
        if (selectedMembers.length === 0 || selectedSessions.length === 0) {
            toast.error(t('gym.bulkEnrollment.errors.selectAtLeastOne'));
            return;
        }

        // Conferma finale
        const totalEnrollments = selectedMembers.length * selectedSessions.length;
        if (!window.confirm(
            t('gym.bulkEnrollment.confirmation.title', {
                count: totalEnrollments,
                users: selectedMembers.length,
                sessions: selectedSessions.length
            })
        )) {
            return;
        }

        setIsProcessing(true);

        try {
            const api = getAuthenticatedApi();
            
            // Usa endpoint bulk enrollment
            const response = await api.post('/gym_courses.php', {
                action: 'bulk_enroll_multiple_sessions',
                user_ids: selectedMembers,
                session_ids: selectedSessions,
                send_notification: sendNotification
            });

            if (response.data.success) {
                const { 
                    total_enrollments_created, 
                    duplicates_skipped, 
                    errors 
                } = response.data;

                let message = `🎉 ${t('gym.bulkEnrollment.success.title')}\n\n`;
                message += `✅ ${t('gym.bulkEnrollment.success.created', { count: total_enrollments_created })}\n`;
                
                if (duplicates_skipped > 0) {
                    message += `⚠️ ${t('gym.bulkEnrollment.success.duplicates', { count: duplicates_skipped })}\n`;
                }
                
                if (errors && errors.length > 0) {
                    message += `\n❌ ${t('gym.bulkEnrollment.success.errors', { count: errors.length })}:\n`;
                    errors.slice(0, 3).forEach(err => {
                        message += `• ${err}\n`;
                    });
                    if (errors.length > 3) {
                        message += `• ... e altri ${errors.length - 3} errori`;
                    }
                }

                message += `\n🔄 La pagina è stata resettata per una nuova operazione.`;

                // Mostra messaggio di successo
                setSuccessMessage(message);
                setShowSuccess(true);

                // Reset completo
                setSelectedMembers([]);
                setSelectedSessions([]);
                setSendNotification(true);

                // Nasconde il messaggio di successo dopo 5 secondi
                setTimeout(() => {
                    setShowSuccess(false);
                    setSuccessMessage('');
                }, 5000);
            }
        } catch (err) {
            console.error('Errore durante iscrizione corsi:', err);
            toast.error(
                `❌ ${t('gym.bulkEnrollment.errors.enrollmentError')}:\n\n` + 
                (err.response?.data?.error || err.message || 'Errore sconosciuto')
            );
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="flex h-screen flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">
            <div className={`shrink-0 border-b border-gray-200 px-4 py-4 dark:border-gray-800 sm:px-6 ${isDark ? gymDarkHeaderBar : 'bg-white'}`}>
                <button
                    type="button"
                    onClick={() => navigate('/gym/members')}
                    className="mb-2 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                >
                    <ArrowLeft size={18} />
                    <span>{t('gym.bulkEnrollment.backToMembers')}</span>
                </button>
                <h1 className="text-2xl font-bold text-gray-900 dark:font-open-sans dark:text-white">
                    {t('gym.bulkEnrollment.title')}
                </h1>
                <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
                    {t('gym.bulkEnrollment.description')}
                </p>
            </div>

            <div className="flex min-h-0 flex-1 flex-col xl:flex-row">
                <aside className="h-[min(440px,48vh)] shrink-0 overflow-hidden border-b border-gray-200 dark:border-gray-800 xl:h-auto xl:w-[400px] xl:border-b-0 xl:border-r">
                    <MemberCheckboxList
                        selectedMembers={selectedMembers}
                        onSelectionChange={setSelectedMembers}
                    />
                </aside>

                <section className="flex min-h-0 min-w-0 flex-1 flex-col bg-gray-50 dark:bg-gray-950">
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <CourseSessionSelector
                            selectedSessions={selectedSessions}
                            onSelectionChange={setSelectedSessions}
                        />
                    </div>
                    <EnrollmentPreview
                        selectedMembers={selectedMembers}
                        selectedSessions={selectedSessions}
                        sendNotification={sendNotification}
                        onToggleNotification={setSendNotification}
                        onConfirm={handleConfirm}
                    />
                </section>
            </div>

                {isProcessing && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center dark:border-white/10 dark:bg-gray-900">
                            <Loader className="animate-spin mx-auto text-blue-600 mb-4" size={48} />
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                {t('gym.bulkEnrollment.processing.title')}
                            </h3>
                            <p className="text-gray-600 dark:text-white/75">
                                {t('gym.bulkEnrollment.processing.message')}
                            </p>
                        </div>
                    </div>
                )}

                {/* Success Message */}
                {showSuccess && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="max-w-lg rounded-lg border border-gray-200 bg-white p-8 text-center dark:border-white/10 dark:bg-gray-900">
                            <div className="text-6xl mb-4">🎉</div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                                {t('gym.bulkEnrollment.success.title')}
                            </h3>
                            <div className="mb-4 rounded-lg bg-gray-50 p-4 text-left dark:bg-gray-800/60">
                                <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-white/90">
                                    {successMessage}
                                </pre>
                            </div>
                            <button
                                onClick={() => {
                                    setShowSuccess(false);
                                    setSuccessMessage('');
                                }}
                                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
                            >
                                Chiudi
                            </button>
                        </div>
                    </div>
                )}
        </div>
    );
}

